import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import ApiHttpClient from "@/HttpClient";
import { toast } from "@/components/ui/use-toast";
import { WarehouseModel, WorkingPointModel } from "@/api/Models";
import { getDecodedTokenDetails } from "@/components/Token/TokenService";
import UserFormPage, {UserFormMode} from "@/components/MyAccount/UserManagement/UserFormPage";

export interface WorkingPointResponse {
    items: WorkingPointModel[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasPrevious: boolean;
    hasNext: boolean;
}

type Role =
    | "BASIC"
    | "BASICSUPPORT"
    | "B2BSUPPORT"
    | "SUPERBASIC"
    | "AUCHANMBAEMPLOYEE"
    | "ADMIN"
    | "SUPPORTL1"
    | "SUPERADMIN"
    | "SERVICE"
    | "SUPPORTL2L3";

const ROLE_HIERARCHY: Record<Role, any> = {
    BASIC: 0,
    BASICSUPPORT: 1,
    B2BSUPPORT: 1,
    SUPERBASIC: 2,
    AUCHANMBAEMPLOYEE: 3,
    ADMIN: 4,
    SUPPORTL1: 5,
    SUPERADMIN: 6,
    SERVICE: 7,
    SUPPORTL2L3: 8,
};

const ROLE_OPTIONS: Role[] = [
    "BASIC",
    "BASICSUPPORT",
    "B2BSUPPORT",
    "SUPERBASIC",
    "AUCHANMBAEMPLOYEE",
    "ADMIN",
    "SUPPORTL1",
    "SUPERADMIN",
    "SERVICE",
    "SUPPORTL2L3",
];

interface UserFormValues {
    accountName: string;
    userName: string;
    password: string;
    phoneNumber: string;
    role: Role;
    uc?: string;
    selectedWorkingPointId?: string;
    pairWorkingPointStore?: string;
}

function AddNewUserPage() {
    const [warehouses, setWarehouses] = useState<any>();
    const [workingPoints, setWorkingPoints] = useState<WorkingPointModel[]>([]);
    const [availableRoles, setAvailableRoles] = useState<string[]>([]);

    useEffect(() => {
        const fetchWorkingPoints = async () => {
            try {
                const response = await ApiHttpClient().post<WorkingPointResponse>(
                    `/workingPoint/search`,
                    {
                        pageNumber: 1,
                        pageSize: 100,
                        orderBy: ["workingPointName"],
                    }
                );
                setWorkingPoints(response.data.items || []);
            } catch (error) {
                console.error("Failed to fetch working points", error);
            }
        };

        fetchWorkingPoints();
    }, []);

    const handleSaveUser = async (formData: UserFormValues) => {
        const {
            accountName,
            userName,
            password,
            phoneNumber,
            role,
            uc,
            selectedWorkingPointId,
            pairWorkingPointStore
        } = formData;

        const body = {
            email: accountName,
            name: userName,
            password: password,
            phoneNumber: phoneNumber,
            role: role,
            claims: (() => {
                const claimsObject: Record<string, string[]> = {};

                if (role === "BASIC" && selectedWorkingPointId) {
                    claimsObject["WPID"] = [selectedWorkingPointId];
                }

                if (
                    (role === "BASICSUPPORT" ||
                        role === "SUPERBASIC" ||
                        role === "B2BSUPPORT" ||
                        role === "ADMIN") &&
                    uc
                ) {
                    claimsObject["ADMINORG"] = [uc];
                }

                if (role === "AUCHANMBAEMPLOYEE" && pairWorkingPointStore) {
                    claimsObject["MBASTORE"] = [pairWorkingPointStore];
                }

                return claimsObject;
            })(),
        };

        try {
            const apiBase = process.env.REACT_APP_AIS_ENDPOINT?.replace(/\/v1\/?$/, '') ?? '';
            await ApiHttpClient().post(`${apiBase}/users`, body);

            toast({ description: "Utilizatorul a fost creat cu succes!" });
        } catch (error) {
            toast({ description: "A apărut o eroare la salvare." });
        }
    };

    function isRole(role: string): role is Role {
        return role in ROLE_HIERARCHY;
    }

    useEffect(() => {
        (async () => {
            try {
                const tokenDetails = await getDecodedTokenDetails();

                if (tokenDetails?.token) {
                    const userRoleStr = tokenDetails.userRole.toString();

                    if (!isRole(userRoleStr)) {
                        console.error("Invalid user role:", userRoleStr);
                        return;
                    }

                    const userRole: Role = userRoleStr;
                    const userRoleValue = ROLE_HIERARCHY[userRole];

                    const filteredRoles = ROLE_OPTIONS.filter(
                        (role) => ROLE_HIERARCHY[role] < userRoleValue
                    );

                    setAvailableRoles(filteredRoles);
                } else {
                    console.error("No token found");
                }
            } catch (error) {
                console.error("Failed to decode token", error);
            }
        })();
    }, []);

    useEffect(() => {
        const fetchWarehouses = async () => {
            let response = await ApiHttpClient().post<WarehouseModel>(`/warehouse/search`, {
                "advancedSearch": {
                    "fields": [
                        "warehousePlatform"
                    ],
                    "keyword": "MAGAZIN",
                },
            });

            setWarehouses(response.data);
        };

        fetchWarehouses();
    }, []);
    
    return (
        <>
            <Navbar />
            <div className="flex flex-row items-start">
                <SideMenu />
                <div className="flex flex-col overflow-auto w-full">
                    <div className="text-4xl px-8 py-3 mt-5">Creeaza un utilizator nou</div>
                    <UserFormPage
                        mode={UserFormMode.ADD}
                        initialValues={{
                            accountName: "",
                            userName: "",
                            password: "",
                            phoneNumber: "",
                            role: "",
                        }}
                        availableRoles={availableRoles}
                        warehouses={warehouses}
                        workingPoints={workingPoints}
                        onSubmit={(data) => {
                            handleSaveUser(data);
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export default AddNewUserPage;
